import { useConfirmOrderMutation, type GetOrderQuery } from '@codezo/graphql';
import { Badge, Thumb } from '@codezo/quote-detail';
import { Box, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { CurrencyInput } from '../../../components/ui/currency-input';
import { VetterSelector } from '../../../components/selectors/VetterSelector';
import { ProductImage, primaryProductImage } from '../../../components/ui/product-image';
import { getMutationError } from '../../../lib/mutation-response';
import { useCurrencyStore } from '../../../stores/currency';
import { useMoney } from '../../quotes/v2/format';

type Order = NonNullable<GetOrderQuery['getOrder']>;
type OrderItem = Order['items'][number];

interface ItemState {
	/**
	 * The cost the admin TYPED. Unset means "the order's own figure", which is
	 * read live from the item rather than copied here — a copy would keep an old
	 * currency's number after the header currency changes.
	 */
	baseUnitPrice?: string;
	vetterEntityId: string;
}

/**
 * Costing the items, as the design draws it: one line per product inside the
 * flow row, not a card of its own.
 *
 * ── Why a row and not a form ──
 *
 * This sits INSIDE the spine, as the content of the "Confirm items" step. A
 * bordered card with its own heading — which is what v1 renders, because there it
 * is a standalone prompt — would put a second frame inside the panel and repeat a
 * title the row above already carries. So each item is one line: what it is, what
 * it costs us, who is making it, and the button that settles it.
 *
 * ── Two prices on one line ──
 *
 * The quiet line under the name is what the CLIENT pays — the unit price the
 * offer was accepted at. The input is what WE pay: the supplier's unit cost,
 * which is the whole point of the step, and the number every margin on the page
 * is measured against. Showing the offered price beside the field is what makes
 * a mistyped cost obvious.
 */
export function ConfirmItemsPanel({
	orderId,
	items,
	onConfirmed,
	footer,
}: {
	orderId: string;
	items: OrderItem[];
	onConfirmed: () => void;
	/** "Skip for now" — the spine folds the panel away rather than removing it. */
	footer?: React.ReactNode;
}) {
	const { selectedCurrency } = useCurrencyStore();
	const money = useMoney();
	const [confirmOrder, { loading }] = useConfirmOrderMutation();
	const [busyItemId, setBusyItemId] = useState<string | null>(null);

	const [states, setStates] = useState<Record<string, ItemState>>(() =>
		Object.fromEntries(
			items.map((item) => [
				item.id,
				{
					// What was recorded at confirmation if there is one, otherwise the
					// vetter the OFFER already named — so an admin confirms a choice
					// rather than making it again from an empty dropdown.
					vetterEntityId: item.confirmedVetterEntityId ?? item.vetterEntityId ?? '',
				},
			])
		)
	);

	/* A cost typed in one currency means nothing in another, and it is saved
	   with the CURRENT currency — so a switch drops what was typed and the
	   fields fall back to the order's figures, which reload in the new one. */
	const typedIn = useRef(selectedCurrency);
	useEffect(() => {
		if (typedIn.current === selectedCurrency) return;
		typedIn.current = selectedCurrency;
		setStates((prev) =>
			Object.fromEntries(
				Object.entries(prev).map(([id, st]) => [id, { vetterEntityId: st.vetterEntityId }])
			)
		);
	}, [selectedCurrency]);

	const update = (itemId: string, patch: Partial<ItemState>) =>
		setStates((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));

	/** What the cost field holds: what was typed, else the order's own cost. */
	const costOf = (item: OrderItem) =>
		states[item.id]?.baseUnitPrice ??
		(item.baseUnitPrice != null ? String(item.baseUnitPrice) : '');

	const valid = (item: OrderItem) => !!costOf(item) && !!states[item.id]?.vetterEntityId;

	const pending = items.filter((item) => !item.confirmedAt);
	const canConfirmAll = pending.length > 0 && pending.every(valid);

	const submit = async (batch: OrderItem[]) => {
		try {
			const { data } = await confirmOrder({
				variables: {
					id: orderId,
					input: {
						items: batch.map((item) => ({
							orderItemId: item.id,
							baseUnitPrice: parseFloat(costOf(item) || '0'),
							currency: selectedCurrency,
							vetterEntityId: states[item.id]?.vetterEntityId || '',
						})),
					},
				},
			});
			const error = getMutationError(data?.confirmOrder);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success(batch.length > 1 ? `${batch.length} items confirmed` : 'Item confirmed');
			onConfirmed();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to confirm');
		}
	};

	const confirmOne = async (item: OrderItem) => {
		setBusyItemId(item.id);
		try {
			await submit([item]);
		} finally {
			setBusyItemId(null);
		}
	};

	return (
		/* No box.
		   This was a bordered panel on sunken ground — the last one left in a spine
		   whose every other row is bare on the card. Against them it read as a
		   different kind of thing rather than as the content of a step, which is
		   what it is. Hairlines between the items are enough to separate them. */
		<div className="mt-2">
			{items.map((item, i) => {
				const s = states[item.id];
				const confirmed = !!item.confirmedAt;

				return (
					<div
						key={item.id}
						className={
							'flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2' +
							(i < items.length - 1 ? ' border-b border-line-1' : '')
						}
					>
						<Thumb size={24}>
							<ProductImage
								image={primaryProductImage(item.productImages)}
								alt=""
								className="h-full w-full"
								fallback={<Box size={11} />}
							/>
						</Thumb>

						<span className="min-w-0 flex-[1_1_140px]">
							<span className="block truncate text-sm font-medium text-text-1">
								{item.productName || 'Product'}
							</span>
							<span className="mt-px block font-mono text-2xs text-text-4">
								{item.unitPrice != null
									? `offered at ${money(item.unitPrice, null, { unit: true })} · qty ${item.quantity.toLocaleString()}`
									: `qty ${item.quantity.toLocaleString()}`}
							</span>
						</span>

						{confirmed ? (
							<Badge tone="success" dot>
								Confirmed
							</Badge>
						) : (
							<>
								<CurrencyInput
									currency={selectedCurrency}
									value={costOf(item)}
									onChange={(e) =>
										update(item.id, {
											baseUnitPrice: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Unit cost"
									min={0}
									step="any"
									className="h-7 w-[112px]"
								/>
								<VetterSelector
									value={s?.vetterEntityId ?? ''}
									onChange={(v) => update(item.id, { vetterEntityId: v })}
									placeholder="Select vetter"
									className="h-7 w-[150px]"
									creatable
								/>
								<Button
									variant="outline"
									size="xs"
									disabled={!valid(item) || busyItemId === item.id || loading}
									onClick={() => confirmOne(item)}
								>
									<Check size={12} data-icon="inline-start" />
									{busyItemId === item.id ? 'Confirming…' : 'Confirm'}
								</Button>
							</>
						)}
					</div>
				);
			})}

			{(canConfirmAll || footer) && (
				<div className="mt-1 flex flex-wrap items-center gap-2 border-t border-line-1 pt-2.5">
					{pending.length > 1 && (
						<Button
							size="xs"
							disabled={!canConfirmAll || !!busyItemId || loading}
							onClick={() => submit(pending)}
						>
							<Check size={12} data-icon="inline-start" />
							{loading && !busyItemId ? 'Confirming…' : 'Confirm all items'}
						</Button>
					)}
					{footer}
				</div>
			)}
		</div>
	);
}
